import { type InternalFile, demoDataFiles, demoImages, getFilesByType } from "@quickpoint.me/shared";
import type { QpFile, ViewFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import { cloneDeep } from "lodash-es";
import { DefaultValue, atom, selector } from "recoil";
import { utilityFileNameMessage } from "../lib/codeMessages";
import imageOrColorKeywordParser from "../lib/imageOrColorKeywordParser";
import { OwnerName } from "./authSelectors";
import { CodeMirrorCodeState } from "./codeAtom";
import { StackCfKeyAtom } from "./stackAtoms";
import { DemoMode } from "./uiAtoms";


export const AddToCurrentlySelectedFilePath = selector<string>({
  key: "AddToCurrentlySelectedFilePath",
  get: () => "",
  set: ({ get, set }, newValue) => {
    let current = get(SelectedFilePath);
    if (current instanceof DefaultValue) return;
    if (newValue instanceof DefaultValue) return;
    // set up fileTree. Root is always ownerName
    const ownerName = get(OwnerName);
    if (ownerName && !current) current = [ownerName];
    set(SelectedFilePath, [...current, newValue]);
  },
});

export const RemoveFromCurrentlySelectedFilePath = selector<string>({
  key: "RemoveFromCurrentlySelectedFilePath",
  get: () => "",
  set: ({ get, set }, newValue) => {
    const current = get(SelectedFilePath).slice();
    if (current instanceof DefaultValue) return;
    if (newValue instanceof DefaultValue) return;
    current.pop(); // can only move backwards one step at a time
    set(SelectedFilePath, [...current]);
  },
});

export const CurrentlySelectedFilePath = selector<string[] | false>({
  key: "CurrentlySelectedFilePath",
  get: ({ get }) => {
    const ownerName = get(OwnerName);
    if (!ownerName || ownerName in utilityFileNameMessage) return false;
    const filePath = get(SelectedFilePath);
    return filePath ? filePath : [ownerName];
  },
});

export const SelectedFilePath = atom<string[]>({
  key: "SelectedFileLevel",
  default: undefined,
});
export const AllFiles = atom<QpFile[]>({
  key: "AllFilesAtom",
  default: [], // Default value is an empty array
  // effects: [
  //   ({ onSet, getLoadable }) => {
  //     const hostId = getLoadable(HostId).getValue();
  //     console.log("AllFiles atom initialized with hostId:", hostId);
  //     onSet((newValue,) => {
  //       if (newValue instanceof DefaultValue) return;

  //       sendAllFiles(hostId, newValue);
  //     });
  //   },
  // ],
});


export const CombinedFileMapsSelector = selector<{
  audioFiles: ViewFile[];
  dataFiles: ViewFile[];
  images: ViewFile[];
}>({
  key: "CombinedFileMapsSelector",
  get: ({ get }) => {
    const stackCfKey = get(StackCfKeyAtom);
    const code = get(CodeMirrorCodeState(stackCfKey));
    const parsed = imageOrColorKeywordParser(code);
    const demoMode = get(DemoMode)

    const allFiles = demoMode ? [...demoImages, ...demoDataFiles] : get(AllFiles);

    // Filter files by type
    const audioFiles = getFilesByType(
      "audio",
      allFiles.filter((file) => parsed.includes(file.userDefinedName))
    );

    const dataFiles = getFilesByType(
      "data",
      allFiles.filter((file) => parsed.includes(file.userDefinedName))
    );

    const imageFiles = getFilesByType(
      "image",
      allFiles.filter((file) => parsed.includes(file.userDefinedName))
    );

    // Clone and format files
    const formatFiles = (files: InternalFile[]) =>
      files.map(({ cloudFlareName, fileName, fileType, id, ownerId, ownerName, stackId, tags, uploadedOn, ...rest }) => rest as InternalFile);

    return {
      audioFiles: formatFiles(cloneDeep(audioFiles) as InternalFile[]),
      dataFiles: formatFiles(cloneDeep(dataFiles) as InternalFile[]),
      images: formatFiles(cloneDeep(imageFiles) as InternalFile[]),
    };
  },
});

export const AvailableImagesSelector = selector<{ name: string; alt: string }[]>({
  key: "AvailableImagesSelector",
  get: ({ get }) => {
    const demoMode = get(DemoMode);
    const allFiles: QpFile[] = demoMode ? (demoImages as QpFile[]) : get(AllFiles);

    const images = getFilesByType("image", allFiles);

    return images.map((file) => ({
      name: file.userDefinedName,
      alt: file.altText || "",
    }));
  },
});

